import _filter from 'lodash/filter';
import _cloneDeep from 'lodash/cloneDeep';
import { RouteContext } from './route';
import * as React from 'react';
import { Provider, ProviderContext } from '../shared-exports';
import { ModelName } from '../models/names';
import { ModelHttpService, ModelStorage } from '../services';
import { DataContext } from './modelCache';
import { isOther } from '../models/modelUtils';
import { RenderBuildingChain } from '../components/utils'

/** Utility method for checking if strings are matching */
const matches = (left: string, right: string) => (left.toLowerCase() == right.toLowerCase());

export enum UpdateCartAction {
	IncrementQuantity,
	DecrementQuantity,
	RemoveProduct,
	AddProduct,
	ToggleSelected
}

interface Interface {
	/** Customer delivery information */
	deliveryInformation: IDeliveryInformation
	/** Information about the currently selected project */
	currentProject?: IProject
	/** Accessor for the current cart contents on the selected floor, or unsorted projects */
	cart: IFloor['cart']
	/** Any entered comments about the ongoing order */
	comments: string
	/** Returns the available materials for a given filtered material type and optional color  */
	findMaterials: (materialFamily: MaterialFamily, color?: ColorFamily) => Array<ISheetMaterial>
	/** Returns the available colors for a given filtered material family  */
	findColors: (materialFamily: MaterialFamily) => Array<ISelectableSheetColor>
	/** Returns the available material types  */
	findSelectableMaterialTypes: () => Array<ISelectableSheetMaterial>
	/** Update the order item contents of the "cart" (a.k.a. current floor) */
	updateCart: (action: UpdateCartAction, product: IOrderItem, item?: IOrderItem, type?:string) => Promise<void>
	/** Creates a new order item, adds it to the cart and returns it for editing */
	createOrderItem: (template: ISheetProductTemplate, product?: ISheetProduct, selected?: boolean) => Promise<IOrderItem>
	/** Creates a new water drainage order item, adds it to the cart and returns it for editing */
	createWaterDrainageOrderItem: (product: IWaterDrainageProduct[], type: string) => Promise<IOrderItem>
	/** Creates a new infasting order item, adds it to the cart and returns it for editing */
	createInfastningOrderItem: (product: IInfastningProduct[], type: string) => Promise<IOrderItem>
	/** Creates a new ovrigt order item, adds it to the cart and returns it for editing */
	createOvrigtOrderItem: (product: IOvrightProduct[], type: string) => Promise<IOrderItem>
	/** Send order to the backend */
	sendOrder: (di: IDeliveryInformation, data?: DataContext) => Promise<boolean>
	/** Adds a floor to the current project */
	addFloor: () => Promise<void>
	/** Adds a building to the current project */
	addBuilding: () => Promise<void>
	/** Adds a facade to the current project */
	addFacade: () => Promise<void>
	/** Adds a roof to the current project */
	addRoof: () => Promise<void>
	addOther: () => Promise<void>
	/** Removes a building */
	removeBuilding: (building: IBuilding | IOther) => Promise<void>
	/** Removes a Fllor */
	removeFloor: (floor: IFloor, parent: IFacade) => Promise<void>
	/** Removes a Facade */
	removeFacade: (facade: IFacade, parent: IBuilding) => Promise<void>
	/** Removes a roof */
	removeRoof: (roof: IRoof, parent: IBuilding) => Promise<void>
	updateUserCartData: (cart: Object) => Promise<void>
	updateCurentProject: (project: Object) => Promise<void>
	updateConnectedUserInProject: (project: Object) => Promise<void>
	updateLocalProjectState: (project: any) => Promise<void>
	updateProjectCartForOtherUser: (data: any) => Promise<void>,
	updateOrderStatus: (orderItem: object, status: string, lastStatus: string) => Promise<void>,
	needToUpdateCurrentProject?: boolean,
	isSocketEnabled?: boolean,
}

const DefaultContext: Interface = {
	cart: [],
	// @ts-expect-error — interface mismatch
	deliveryInformation: {
		address: {
			city: "",
			zipcode: "",
			street: ""
		},
		notes: "",
		name: "",
		email: "",
		deliveryCharge: 0,
		deliveryDate: new Date()
	},
	comments: "",
	findMaterials: null!,
	findColors: null!,
	findSelectableMaterialTypes: null!,
	updateCart: null!,
	addBuilding: null!,
	addFacade: null!,
	addRoof: null!,
	addFloor: null!,
	addOther: null!,
	createOrderItem: null!,
	createWaterDrainageOrderItem: null!,
	createInfastningOrderItem: null!,
	createOvrigtOrderItem: null!,
	sendOrder: null!,
	removeBuilding: null!,
	removeFacade: null!,
	removeFloor: null!,
	removeRoof: null!,
	updateUserCartData: null!,
	updateCurentProject: null!,
	updateConnectedUserInProject: null!,
	updateLocalProjectState: null!,
	needToUpdateCurrentProject: false,
	updateProjectCartForOtherUser: null!,
	isSocketEnabled: false,
	updateOrderStatus: null!
}

// Types used for caching of project selections.
type BuildingSelectionKeys = 'selectedFacade' | 'selectedFloorOrRoof';
interface BuildingSelections extends Pick<IProject, BuildingSelectionKeys> { }

/** The context that will be used by the provider */
export const UserContext: React.Context<Interface> = React.createContext(DefaultContext);
export interface UserContext extends ProviderContext<Interface> { };

interface Props { }
interface State { }

/** Provider for data related to the current user of the application */
export class UserProvider extends Provider<Interface, Props, State> {

	protected data!: DataContext
	private router!: RouteContext

	/** Exposes the context to use */
	protected use() { return UserContext }

	/** Overrides base render to consume DataContext and RouteContext */
	public render() {
		const Context = this.use();
		return (
			<DataContext.Consumer>
				{data => {
					this.data = data as DataContext;
					return (
						<RouteContext.Consumer>
							{router => {
								this.router = router as RouteContext;
								return (
									<Context.Provider value={this.state}>
										{this.props.children}
									</Context.Provider>
								);
							}}
						</RouteContext.Consumer>
					);
				}}
			</DataContext.Consumer>
		);
	}

	private readonly selectionsCache: Map<IBuilding, BuildingSelections> = new Map;
	private previousBuilding: IBuilding | undefined

	/** Declares the initial state */
	protected initialState(): Interface & State {
		return {
			...DefaultContext,
			findMaterials: this.findMaterials.bind(this),
			findColors: this.findColors.bind(this),
			findSelectableMaterialTypes: this.findSelectableMaterialTypes.bind(this),
			updateCart: this.updateCart.bind(this),
			addFloor: this.addFloor.bind(this),
			addBuilding: this.addBuilding.bind(this),
			addFacade: this.addFacade.bind(this),
			addRoof: this.addRoof.bind(this),
			addOther: this.addOther.bind(this),
			createOrderItem: this.createOrderItem.bind(this),
			// @ts-expect-error — interface mismatch
			createWaterDrainageOrderItem: this.createWaterDrainageOrderItem.bind(this),
			// @ts-expect-error — interface mismatch
			createInfastningOrderItem: this.createInfastingOrderItem.bind(this),
			// @ts-expect-error — interface mismatch
			createOvrigtOrderItem: this.createOvrigtOrderItem.bind(this),
			sendOrder: this.sendOrder.bind(this),
			// @ts-expect-error — interface mismatch
			removeBuilding: this.removeBuilding.bind(this),
			removeFacade: this.removeFacade.bind(this),
			removeFloor: this.removeFloor.bind(this),
			removeRoof: this.removeRoof.bind(this),
			updateUserCartData: this.updateUserCartData.bind(this),
			updateCurentProject: this.updateCurentProject.bind(this),
			updateConnectedUserInProject: this.updateConnectedUserInProject.bind(this),
			updateLocalProjectState: this.updateLocalProjectState.bind(this),
			// @ts-expect-error — interface mismatch
			updateProjectCartForOtherUser: this.updateProjectCartForOtherUser.bind(this),
			updateOrderStatus: this.updateOrderStatus.bind(this)

		}
	}

	async componentDidMount() {
		// Set the correct cart on mount
		const { currentUser } = this.data;
		if (this.data.currentUser && this.data.currentUser.superAdmin) {
			return;
		}
		if (this.data.currentUser) {
			await this.setState({ cart: currentUser.unsortedProducts });
			const service = new ModelHttpService;
			this.data.setUserTypeAccess();
			this.data.fetchProjectData();

			// Fetch profile and unsorted products in parallel
			const [userData, unsortedRes] = await Promise.all([
				service.getUserProfile<IUser>(ModelName.User, this.data.currentUser._id),
				service.request<{ user: IUser }>("/api/users/getUnsortedProduct", "post", {username : this.data.currentUser.email}),
			]);

			// Apply results sequentially to avoid state race conditions
			if (userData && userData.profile) {
				currentUser.profile = userData.profile;
			}
			if (unsortedRes && unsortedRes.user && unsortedRes.user.unsortedProducts) {
				currentUser.unsortedProducts = unsortedRes.user.unsortedProducts;
			}
			this.data.updateState({ currentUser });
		}
		// Get saved project from local storage
		const project = ModelStorage.getItem('project');
		if (project != null) {
			const currentProject = JSON.parse(project);
			if (this.router.view.path === "/project/unsorted") {
				return;
			}
			await this.setState({ currentProject })
			if (this.state.currentProject!.selectedFloorOrRoof && this.router.view.path === "/project/cart") {
				await this.setState({ cart: this.state.currentProject!.selectedFloorOrRoof.cart || [] });
			}
			if (__DEV__) return
			if (this.state.currentProject!.selectedFloorOrRoof) {
				await this.router.push('/project/cart', 'slide-down')
			} else {
				await this.router.push('/project/details', 'slide-down')
			}
		}
	}

	/** Getter/Setter for accessing the current cart if possible */
	private async cart(newCart?: IFloor['cart']) {
		const { currentProject } = this.state;
		const { currentUser } = this.data;
		
		if (currentProject && currentProject.selectedOtherBuilding) {
			let needToUpdateCurrentProject = false
			if (newCart) {
				if (newCart.length != currentProject.selectedOtherBuilding.cart.length) {
					needToUpdateCurrentProject = true;
				}
				currentProject.selectedOtherBuilding.cart = newCart;
				await this.updateState({ currentProject });
				this.updateProjectCartForOtherUser(currentProject);
			}

			if (needToUpdateCurrentProject) {
				this.updateCurentProject(currentProject || {})
			}
			// Return the selected floors cart
			return currentProject.selectedOtherBuilding.cart;
		} else if (currentProject && currentProject.selectedFloorOrRoof) { // Access the cart of the currently selected floor
			let needToUpdateCurrentProject = false
			if (newCart) {
				if (newCart.length != currentProject.selectedFloorOrRoof.cart.length) {
					needToUpdateCurrentProject = true;
				}
				currentProject.selectedFloorOrRoof.cart = newCart;
				await this.updateState({ currentProject });
				this.updateProjectCartForOtherUser(currentProject);
			}

			// Update the server version of the project
			// update cart called to backend which not necessary when user select & unselect product
			// await new ModelHttpService().update(ModelName.Project, currentProject);

			if (needToUpdateCurrentProject) {
				this.updateCurentProject(currentProject || {})
			}
			// Return the selected floors cart
			return currentProject.selectedFloorOrRoof.cart;
		}

		// Use the current users unsorted products as cart
		else {
			if (newCart) {
				currentUser.unsortedProducts = newCart;
				this.data.updateState({ currentUser });

				// Update the server version of the user
				await new ModelHttpService().update(ModelName.User, currentUser);
			}

			// Return the unsorted products
			return currentUser.unsortedProducts;
		}
	}

	private async removeBuilding(building: IBuilding) {
		if (!isOther(building)) {
			// Remove all facades
			while (building.facades[0]) {
				const facade = building.facades[0]
				await this.removeFacade(facade, building)
			}
			const currentProject = this.state.currentProject!;
			currentProject.buildings = currentProject.buildings.filter(b => b !== building)
		} else {
			const service = new ModelHttpService;
			for (const order of building.cart) {
				if (order.ordered) {
					continue
				}
				await service.delete<IOrderItem>(ModelName.OrderItem, order)
			}
			const currentProject = this.state.currentProject!;
			currentProject.others = currentProject.others.filter((other) => other.name !== building.name);
		}
	}


	private async removeRoof(roof: IRoof, parent: IBuilding): Promise<void> {
		// Remove all orders
		const service = new ModelHttpService;
		for (const order of roof.cart) {
			if (order.ordered) {
				continue
			}
			await service.delete<IOrderItem>(ModelName.OrderItem, order)
		}
		parent.roofs = parent.roofs.filter(f => f !== roof)
	}

	private async removeFacade(facade: IFacade, parent: IBuilding) {
		// Remove all floors
		while (facade.floors[0]) {
			const floor = facade.floors[0];
			await this.removeFloor(floor, facade);
		}
		parent.facades = parent.facades.filter(f => f !== facade);
	}

	private async removeFloor(floor: IFloor, parent: IFacade) {
		// Remove all orders
		const service = new ModelHttpService;
		for (const order of floor.cart) {
			if (order.ordered) {
				continue
			}
			await service.delete<IOrderItem>(ModelName.OrderItem, order)
		}
		parent.floors = parent.floors.filter(f => f !== floor)
	}

	/** Send order to the backend */
	private async sendOrder(deliveryInformation: IDeliveryInformation, data?: DataContext): Promise<boolean> {
		// 1. Assemble payload from cart and deliveryinformation
		// 2. Send order to /api/order
		// 3. Handle statuscode
		// 	- 200: Remove items from cart that was ordered.
		// 	- 5xx: Show error in some way
		const cart = await this.cart();
		let project: any = {};
		if (this.state.currentProject) {
			project.name = this.state.currentProject.name;
			project.owner = { ...this.state.currentProject.owner, profile: null, previousPurchases: [], unsortedProducts: []};
			project.buildings = "";
			cart.filter(i => i.selected).map(i => {
				if (this.state.currentProject)  {
					if (this.state.currentProject.selectedOtherBuilding) {
						project.buildings = this.state.currentProject.selectedOtherBuilding.name;
					} else {
						project.buildings = RenderBuildingChain(this.state.currentProject, i);
					}
					this.setState({ currentProject: this.state.currentProject });
				}
			})
			project.address = this.state.currentProject.address;
			project.reference = this.state.currentProject.reference;
			project.city = this.state.currentProject.city;
		} else {
			project = {
				buildings: [],
				name: "Verkstaden",
				owner: (() => {
					const {firstname, lastname} = this.data.currentUser
					return `${firstname} ${lastname}`
				})(),
				address: deliveryInformation.address,
			}
		};

		let currentUserData = { ...this.data.currentUser, profile: null, previousPurchases: [], unsortedProducts: [] };
		const payload = JSON.stringify({
			project,
      		cart: cart.filter(i => i.selected).map(i => ({...i, ordered: new Date(), deliveryInformation })),
			user: currentUserData,
			reference: deliveryInformation.reference,
			address: deliveryInformation.address.street
		});
		// console.log('orderd', JSON.parse(payload));
		
		const res = await fetch('/api/order', {
			method: "POST",
			body: payload,
			headers: {
				"Content-Type": "application/json",
				'Authorization': localStorage.getItem("token") || "" 
			}
		});
		switch (res.status) {
			case 200:
				const cart = await this.cart();
				let ordered = cart.filter(item => item.selected);
				const currentProject = this.state.currentProject;
				let totalRemainingOrderStatus = 0;
				let remainingItems = 0;
				ordered = ordered.map((order) => {
					order.deliveryInformation = deliveryInformation;
					order.selected = false
					order.ordered = new Date
					order.orderedBy = this.data.currentUser._id;
					return order;
				});
				const newCart = cart.map(x => {
					if (x.selected) {
						x.ordered = new Date();
						x.selected = false;
					}
					return x;
				});
				totalRemainingOrderStatus = newCart.filter((item) => item.ordered && !item.status).length || 0;
				remainingItems = newCart.filter((item) => !item.ordered && !item.status).length || 0;
				if (currentProject && currentProject.selectedOtherBuilding) {
					const prevPurch = [...(currentProject.previousPurchases || []), ordered]
					currentProject.previousPurchases = prevPurch
					currentProject.selectedOtherBuilding.status.totalRemainingOrderStatus = totalRemainingOrderStatus;	
					currentProject.selectedOtherBuilding.status.remainingItems = remainingItems;	
					await this.updateState({ currentProject });
					await new ModelHttpService().update(ModelName.Project, currentProject);
				} else if (currentProject && currentProject.selectedFloorOrRoof) {
					const prevPurch = [...(currentProject.previousPurchases || []), ordered]
					currentProject.previousPurchases = prevPurch;
					currentProject.selectedFloorOrRoof.status.totalRemainingOrderStatus = totalRemainingOrderStatus;	
					currentProject.selectedFloorOrRoof.status.remainingItems = remainingItems;
					await this.updateState({ currentProject });
					await new ModelHttpService().update(ModelName.Project, currentProject);
				} else if (data) {
					const prevPurch = [...(data.currentUser!.previousPurchases || []), ordered]
					data.currentUser!.previousPurchases = prevPurch
					await data.updateState({ currentUser: data.currentUser })
				}
				await this.cart(newCart);
				// console.log('newcart',newCart);
				await this.updateState({ comments: "", currentProject });
				return true;
			default:
				return false;
		}
	
}

	/** Returns the available materials for a given material family and optional color  */
	private findMaterials(materialFamily: MaterialFamily, colorFamily?: ColorFamily): Array<ISheetMaterial> {
		return _filter(this.data.materials, (material => {
			const materialFamilyMatch = material.selectableMaterial.family ? matches(material.selectableMaterial.family, materialFamily) : false;
			const colorFamilyMatch = colorFamily ? (material.selectableColor.family ? matches(material.selectableColor.family, colorFamily) : false) : true;
			return (materialFamilyMatch && colorFamilyMatch);
		}));
	}

	/** Returns a list of all available colros for a given material family  */
	private findColors(materialFamily: MaterialFamily): Array<ISelectableSheetColor> {
		return this.data.selectableColors; //filter(selectableColors, (target) => find(materials, material => material.selectableMaterial && material.selectableMaterial.family == materialFamily && material.selectableColor && material.selectableColor.family == target.family)) as Array<SelectableSheetColor>;
		// NOTE: filtering is disabled by request
	}

	/** Returns a list of all available material types that there exists a material for */
	private findSelectableMaterialTypes(): Array<ISelectableSheetMaterial> {
		return this.data.selectableMaterials; //filter(selectableMaterials, (target) => find(materials, material => material.selectableMaterial && material.selectableMaterial.family == target.family)) as Array<SelectableSheetMaterial>;
		// NOTE: filtering is disabled by request
	}

	/** Creates a new order item and returns it, it is not added to the cart */
	private async createOrderItem(template: ISheetProductTemplate, product?: ISheetProduct, selected?: boolean) {
		return this.data.createOrderItem({ template, product, selected });
	}

	// /** Creates a new water drainage order item and returns it, it is not added to the cart */
	private async createWaterDrainageOrderItem(product: IWaterDrainageProduct[]) {
		// @ts-expect-error — interface mismatch
		return this.data.createWaterDrainageOrderItem(product);
	}

	// /** Creates a new infasting order item and returns it, it is not added to the cart */
	private async createInfastingOrderItem(product: IInfastningProduct[]) {
		// @ts-expect-error — interface mismatch
		return this.data.createInfastingOrderItem(product);
	}

	// /** Creates a new ovrigt order item and returns it, it is not added to the cart */
	private async createOvrigtOrderItem(product: IOvrightProduct[]) {
		// @ts-expect-error — interface mismatch
		return this.data.createOvrigtOrderItem(product);
	}

	private updateProjectCartForOtherUser (project: any) {
		if (this.data && this.data.socket) {
			this.data.socket.emit('update-project', { projectId: project._id, project });
		}
	}
	private async updateCart(action: UpdateCartAction, product: any, orderItem?: IOrderItem, type?:string): Promise<void> {
		let newCart = await this.cart();
		let removeProduct = null;
		let productIndex: number;
		if (action !== UpdateCartAction.AddProduct) {
			if (orderItem) {
				productIndex = newCart.findIndex(i => i._id == orderItem._id)
				if (productIndex == -1) {
					throw new Error(`Could not find index of item: ${product}`)
				}
			} else {
				productIndex = -1;
				type && type === 'waterDrainage' ? 
					newCart.map((i: any, index) => {
						if (i._id === product._id) {
							productIndex = index
						}
					}) 
				:
				type && type === 'infästning' ? 
					newCart.map((i: any, index) => {
						if (i._id === product._id) {
							productIndex = index
						}
					}) 
				:
				type && type === 'övrigt' ? 
					newCart.map((i: any, index) => {
						if (i._id === product._id) {
							productIndex = index
						}
					}) 
				:
				newCart.map((i: any, index) => {
					if (i._id === product._id) {
						productIndex = index
					}
				});
				if (productIndex == -1) {
					throw new Error(`Could not find index of item: ${product}`)
				}
			}
		}
		switch (action) {
			case UpdateCartAction.DecrementQuantity:
				// console.log('DecrementQuantity',productIndex!)
				newCart[productIndex!].quantity > 1 && newCart[productIndex!].quantity--;
				break;
			case UpdateCartAction.IncrementQuantity:
				// console.log('IncrementQuantity',productIndex!)
				newCart[productIndex!].quantity++;
				break;
			case UpdateCartAction.RemoveProduct:
				// console.log('RemoveProduct',productIndex!)
				removeProduct = newCart[productIndex!];
				newCart = newCart.filter((_, i) => i != productIndex!);
				break;
			case UpdateCartAction.AddProduct:
				// console.log('AddProduct',orderItem)
				if (orderItem) {
					orderItem.selected = true;
				}

				if (newCart && newCart.length) {
					newCart = newCart.map((cart) => {
						if (cart.selected) {
							cart.selected = false;
						}
						return cart;
					});
				}
				const template = orderItem && 
					(orderItem.type === "waterDrainage" || orderItem.type === "infästning" || orderItem.type === "övrigt") ? 
					  undefined 
					  : await this.data.findTemplate(product.template);
				let item: any = []
				
				if(orderItem && orderItem.waterDrainageProduct && orderItem.type === "waterDrainage"){
					item = orderItem || await this.createWaterDrainageOrderItem(orderItem)
				} else if(orderItem && orderItem.infastingProduct && orderItem.type === "infästning"){
					item = orderItem || await this.createInfastingOrderItem(orderItem)
				} else if(orderItem && orderItem.ovrigtProduct && orderItem.type === "övrigt"){
					item = orderItem || await this.createOvrigtOrderItem(orderItem)
				} else{
					item = orderItem || await this.createOrderItem(template!, product, undefined);
				}
				let addedBy = this.data.currentUser.firstname+' '+this.data.currentUser.lastname;
				item = Object.assign({addedBy}, item);
				if (!newCart) {
					newCart = [];
				}
				if (item._id == undefined) {
					item = await new ModelHttpService().create(ModelName.OrderItem, item);
					item = Object.assign({selected : true}, item);
					newCart = [...newCart || [], item];
				} else {
					await new ModelHttpService().update(ModelName.OrderItem, item);
					newCart = [...newCart.filter(i => i._id != item._id), item];
				}
				// Store the ID of the just-added product so CartView can highlight only this one
				if (item._id) {
					sessionStorage.setItem('lastAddedProductId', item._id);
				}

				const { currentProject } = this.state;
				if (currentProject) {
					if (currentProject.selectedBuilding) {
						if (currentProject.selectedFloorOrRoof) {
							let cartFilter = newCart.filter((prod) => !prod.ordered).length || 0;
							currentProject.selectedFloorOrRoof.status.remainingItems = cartFilter;
						} else if (currentProject.selectedOtherBuilding) {
							let cartFilter = newCart.filter((prod) => !prod.ordered).length || 0;
							currentProject.selectedOtherBuilding.status.remainingItems = cartFilter
						}
					}
				}
				this.updateProjectCartForOtherUser({ cart: newCart, action: UpdateCartAction[action]});
				break;
			case UpdateCartAction.ToggleSelected:
				// console.log('ToggleSelected',productIndex!)
				newCart[productIndex!].selected = !newCart[productIndex!].selected;
				break;
		}
		if (UpdateCartAction.IncrementQuantity === action || UpdateCartAction.DecrementQuantity === action) {
			new ModelHttpService().update(ModelName.OrderItem, { _id: newCart[productIndex!]._id, quantity: newCart[productIndex!].quantity});
			this.cart(newCart);
			return;
		}
		// console.log('newCart exit',newCart)
		// update state only -> when selecting/deselecting product in cart
		if (UpdateCartAction.ToggleSelected === action) {
			const { currentProject } = this.state;

			if (currentProject && currentProject.selectedOtherBuilding) {
				currentProject.selectedOtherBuilding.cart = newCart;
				await this.setState(this.state as Interface);
				// update cart state
				await this.updateState({ cart: newCart })
			}
			// Access the cart of the currently selected floor
			if (currentProject && currentProject.selectedFloorOrRoof) {
				currentProject.selectedFloorOrRoof.cart = newCart;
				await this.setState(this.state as Interface);
				// update cart state
				await this.updateState({ cart: newCart })
			}

			if (this.router.view.path === "/project/unsorted") {
				// Access the cart of the currently selected floor
				if (this.data.currentUser && this.data.currentUser.unsortedProducts.length) {
					await this.cart(newCart)
				}		
			}
			await new ModelHttpService().update(ModelName.OrderItem, { _id: newCart[productIndex!]._id, selected: newCart[productIndex!].selected});
			return;
		}
		if (UpdateCartAction.RemoveProduct === action) {
			if (this.router.view.path === "/project/unsorted") {

				// Access the cart of the currently selected floor
				if (this.data.currentUser && this.data.currentUser.unsortedProducts.length) {
					await this.cart(newCart)
					return
				}		
			} else {
				const { currentProject } = this.state;
				if (currentProject && currentProject.selectedOtherBuilding) {
					if (removeProduct && removeProduct.ordered) {
						let felFilter = newCart.filter((prod) => prod.ordered && prod.status === "Fel").length || 0;
						currentProject.selectedOtherBuilding.status.totalFelOrderStatus = felFilter;
						let monteredFilter = newCart.filter((prod) => prod.ordered && prod.status === "Monterad").length || 0;
						currentProject.selectedOtherBuilding.status.totalMonteradOrderStatus = monteredFilter;
						let levereradFilter = newCart.filter((prod) => prod.ordered && prod.status === "Levererad").length || 0;
						currentProject.selectedOtherBuilding.status.totalLevereradOrderStatus = levereradFilter;
						let tillverkadFilter = newCart.filter((prod) => prod.ordered && prod.status === "Tillverkad").length || 0;
						currentProject.selectedOtherBuilding.status.totalTillverkadOrderStatus = tillverkadFilter;
						let remainingOrderFilter = newCart.filter((prod) => prod.ordered && !prod.status).length || 0;
						currentProject.selectedOtherBuilding.status.totalRemainingOrderStatus = remainingOrderFilter;
					}
					currentProject.selectedOtherBuilding.cart = newCart;
					let cartFilter = newCart.filter((prod) => !prod.ordered).length || 0;
					currentProject.selectedOtherBuilding.status.remainingItems = cartFilter;
					
					await this.setState(this.state as Interface);
					// update cart state
					await this.updateState({ currentProject })
					this.updateProjectCartForOtherUser(currentProject);					
					return
				}
				// Access the cart of the currently selected floor
				if (currentProject && currentProject.selectedFloorOrRoof) {
					if (removeProduct && removeProduct.ordered) {
						let felFilter = newCart.filter((prod) => prod.ordered && prod.status === "Fel").length || 0;
						currentProject.selectedFloorOrRoof.status.totalFelOrderStatus = felFilter;
						let monteradFilter = newCart.filter((prod) => prod.ordered && prod.status === "Monterad").length || 0;
						currentProject.selectedFloorOrRoof.status.totalMonteradOrderStatus = monteradFilter;
						let levereradFilter = newCart.filter((prod) => prod.ordered && prod.status === "Levererad").length || 0;
						currentProject.selectedFloorOrRoof.status.totalLevereradOrderStatus = levereradFilter;
						let tillverkadFilter = newCart.filter((prod) => prod.ordered && prod.status === "Tillverkad").length || 0;
						currentProject.selectedFloorOrRoof.status.totalTillverkadOrderStatus = tillverkadFilter;
						let remainingOrderFilter = newCart.filter((prod) => prod.ordered && !prod.status).length || 0;
						currentProject.selectedFloorOrRoof.status.totalRemainingOrderStatus = remainingOrderFilter;
					}
					currentProject.selectedFloorOrRoof.cart = newCart;
					let cartFilter = newCart.filter((prod) => !prod.ordered).length || 0;
					currentProject.selectedFloorOrRoof.status.remainingItems = cartFilter;
					await this.setState(this.state as Interface);
					// update cart state
					await this.updateState({ currentProject })
					this.updateProjectCartForOtherUser(currentProject);					
					return
				}
			}
			return;
		}
		// Save the new cartw
		await this.cart(newCart);
	}

	private async updateOrderStatus(orderItem: any, orderStatus: string, lastStatus: string): Promise<void> {
		let newCart = await this.cart();
		let productIndex = newCart.findIndex(i => i._id == orderItem._id)
        newCart[productIndex].status = orderStatus;
		if (this.router.view.path === "/project/unsorted") {
			// Access the cart of the currently selected floor
			if (this.data.currentUser && this.data.currentUser.unsortedProducts.length) {
				await this.updateState({ cart: newCart})
				return
			}		
		} else {
			const { currentProject } = this.state;

			if (currentProject && currentProject.selectedOtherBuilding) {
				currentProject.selectedOtherBuilding.cart = newCart;
				let felCount = newCart.filter((prod) => prod.ordered && prod.status === "Fel").length || 0;
				currentProject.selectedOtherBuilding.status.totalFelOrderStatus = felCount;
				let monteradCount = newCart.filter((prod) => prod.ordered && prod.status === "Monterad").length || 0;
				currentProject.selectedOtherBuilding.status.totalMonteradOrderStatus = monteradCount;
				let levereradCount = newCart.filter((prod) => prod.ordered && prod.status === "Levererad").length || 0;
				currentProject.selectedOtherBuilding.status.totalLevereradOrderStatus = levereradCount;
				let tillverkadCount = newCart.filter((prod) => prod.ordered && prod.status === "Tillverkad").length || 0;
				currentProject.selectedOtherBuilding.status.totalTillverkadOrderStatus = tillverkadCount;
				let remainingOrderCount = newCart.filter((prod) => prod.ordered && !prod.status).length || 0;
				currentProject.selectedOtherBuilding.status.totalRemainingOrderStatus = remainingOrderCount;
				let cartFilter = newCart.filter((prod) => !prod.ordered).length || 0;
				currentProject.selectedOtherBuilding.status.remainingItems = cartFilter;
				await this.setState(this.state as Interface);
				// update cart state
				await this.updateState({ currentProject });
				await new ModelHttpService().update(ModelName.Project, currentProject);
				this.updateProjectCartForOtherUser(currentProject);					
				return
			}
			// Access the cart of the currently selected floor
			if (currentProject && currentProject.selectedFloorOrRoof) {
				currentProject.selectedFloorOrRoof.cart = newCart;
				let felCount = newCart.filter((prod) => prod.ordered && prod.status === "Fel").length || 0;
				currentProject.selectedFloorOrRoof.status.totalFelOrderStatus = felCount;
				let monteradCount = newCart.filter((prod) => prod.ordered && prod.status === "Monterad").length || 0;
				currentProject.selectedFloorOrRoof.status.totalMonteradOrderStatus = monteradCount;
				let levereradCount = newCart.filter((prod) => prod.ordered && prod.status === "Levererad").length || 0;
				currentProject.selectedFloorOrRoof.status.totalLevereradOrderStatus = levereradCount;
				let tillverkadCount = newCart.filter((prod) => prod.ordered && prod.status === "Tillverkad").length || 0;
				currentProject.selectedFloorOrRoof.status.totalTillverkadOrderStatus = tillverkadCount;
				let remainingOrderCount = newCart.filter((prod) => prod.ordered && !prod.status).length || 0;
				currentProject.selectedFloorOrRoof.status.totalRemainingOrderStatus = remainingOrderCount;
				let cartFilter = newCart.filter((prod) => !prod.ordered).length || 0;
				currentProject.selectedFloorOrRoof.status.remainingItems = cartFilter;
				await this.setState(this.state as Interface);
				// update cart state
				await this.updateState({ currentProject });
				await new ModelHttpService().update(ModelName.Project, currentProject);
				this.updateProjectCartForOtherUser(currentProject);					
				return
			}
			return;
		}
	}

	/** Adds a floor to the current project */
	private async addFloor() {
		const mhttp = new ModelHttpService();

		if (this.state.currentProject == undefined || this.state.currentProject.selectedFacade == undefined) {
			return
		}
		const name = `Våning ${this.state.currentProject.selectedFacade.floors.length}`;
		const newFloor: IFloor = {
			name,
			cart: [],
			notes: "",
			createdAt: new Date(),
			status: {
				remainingItems: 0,
				totalTillverkadOrderStatus: 0,
				totalRemainingOrderStatus: 0,
				totalFelOrderStatus: 0,
				totalLevereradOrderStatus: 0,
				totalMonteradOrderStatus: 0,
			}
		};
		this.state.currentProject.selectedFacade.floors = [newFloor, ...this.state.currentProject.selectedFacade.floors];
		await mhttp.update(ModelName.Project, this.state.currentProject);
		this.updateState({ currentProject: this.state.currentProject })
	}

	/** Adds a building to the current project */
	private async addBuilding() {
		const mhttp = new ModelHttpService();
		if (this.state.currentProject == undefined) {
			return
		}
		const name = `Hus ${this.state.currentProject.buildings.length + 1}`
		this.state.currentProject.buildings.push({ name, facades: [], roofs: [], createdAt: new Date() });
		await mhttp.update(ModelName.Project, this.state.currentProject);
		this.updateState({ currentProject: this.state.currentProject })
	}

	/** Adds a facade or roof to the current project */
	private async addFacade() {
		const mhttp = new ModelHttpService();
		if (this.state.currentProject == undefined || this.state.currentProject.selectedBuilding == undefined) {
			return
		}
		const name = `Fasad ${this.state.currentProject.selectedBuilding.facades.length + 1}`
		this.state.currentProject.selectedBuilding.facades.push({ name, floors: [], isRoof: false, createdAt: new Date() });
		await mhttp.update(ModelName.Project, this.state.currentProject);
		this.updateState({ currentProject: this.state.currentProject })
	}

	private async addRoof() {
		const mhttp = new ModelHttpService();
		if (this.state.currentProject == undefined || this.state.currentProject.selectedBuilding == undefined) {
			return
		}
		const name = `Tak ${this.state.currentProject.selectedBuilding.roofs.length + 1}`
			this.state.currentProject.selectedBuilding.roofs.push({ name, notes: '', cart: [], isRoof: true, createdAt: new Date(), 
			status: {
				remainingItems: 0,
				totalTillverkadOrderStatus: 0,
				totalRemainingOrderStatus: 0,
				totalFelOrderStatus: 0,
				totalLevereradOrderStatus: 0,
				totalMonteradOrderStatus: 0,
			}
		});
		await mhttp.update(ModelName.Project, this.state.currentProject);
		this.updateState({ currentProject: this.state.currentProject })
	}

	private async addOther() {
		const mhttp = new ModelHttpService();
		if (this.state.currentProject == undefined || this.state.currentProject.selectedBuilding == undefined) {
			return
		}
		const name = `Övrigt ${this.state.currentProject.others.length + 1}`;
		this.state.currentProject.others.push({ name, notes: '', cart: [], isOther: true, createdAt: new Date(),
			status: {
				remainingItems: 0,
				totalTillverkadOrderStatus: 0,
				totalRemainingOrderStatus: 0,
				totalFelOrderStatus: 0,
				totalLevereradOrderStatus: 0,
				totalMonteradOrderStatus: 0,
			}
		});
		await mhttp.update(ModelName.Project, this.state.currentProject);
		this.updateState({ currentProject: this.state.currentProject })
	}

	/** Updates the state of the provider */
	protected async updateState(state: Partial<Interface> | Interface) {
		// Make sure that selections are cached
		if (
			state.currentProject && state.currentProject.selectedBuilding &&
			state.currentProject.selectedBuilding == this.previousBuilding
		) {
			const key = state.currentProject.selectedBuilding
			const values: BuildingSelections = {
				selectedFloorOrRoof: state.currentProject.selectedFloorOrRoof,
				selectedFacade: state.currentProject.selectedFacade,
			}
			this.selectionsCache.set(key, values);
		}

		// Get selections from cache if they exist
		if (
			state.currentProject && state.currentProject.selectedBuilding &&
			state.currentProject.selectedBuilding != this.previousBuilding && !state.currentProject.selectedOtherBuilding
		) {
			const key = state.currentProject.selectedBuilding
			const values = this.selectionsCache.get(key)
			if (values) {
				state.currentProject.selectedFacade = values.selectedFacade
				state.currentProject.selectedFloorOrRoof = values.selectedFloorOrRoof
			}
		}

		if (state.currentProject) {
			this.previousBuilding = state.currentProject.selectedBuilding
		}

		// Store selected info in local storage
		if (state.currentProject || this.state.currentProject) {

			// ModelStorage.setItem('project', JSON.stringify(project));
		}

		// Auto select building parts if len is 0
		if (state.currentProject || this.state.currentProject) {
			const project = (state.currentProject || this.state.currentProject) as IProject;
			(function () {
				// Return if zero or more then one buildings
				if (project.buildings && project.buildings.length) {
					project.selectedBuilding = project.selectedBuilding || project.buildings[0]
				}
				// Return if zero or more then one facades
				if (project.selectedBuilding && project.selectedBuilding.facades) {
				  if (!project.selectedBuilding!.facades.length || project.selectedBuilding!.facades.length > 1) {
					return;
				  }
				}
				if (project.selectedBuilding && project.selectedBuilding.facades && !(project.selectedFloorOrRoof && (project.selectedFloorOrRoof as IRoof).isRoof)) {
					project.selectedFacade = project.selectedBuilding!.facades[0]
				}
			})()
		}

		// Update the accessor for the current cart contents
		await this.setState(state as Interface)
		if (this.state.currentProject) {
			if (this.state.currentProject.selectedFloorOrRoof) {
				state.cart = this.state.currentProject.selectedFloorOrRoof.cart;
			}
			if (this.state.currentProject.selectedOtherBuilding) {
				state.cart = this.state.currentProject.selectedOtherBuilding.cart;
			}
			if (state.currentProject) {
				await new ModelHttpService().update(ModelName.Project, state.currentProject);
			}
		}
		else if (this.data.currentUser) {
			state.cart = this.data.currentUser.unsortedProducts;
		}

		// if (state.needToUpdateCurrentProject) { 
		// 	this.updateCurentProject(state.currentProject || {})
		// }

		if (this.data.socket && state.currentProject && !this.state.isSocketEnabled) {
			state.isSocketEnabled = true;
			this.data.socket.on(`project-${state.currentProject._id}`, async (data: any) => {
				if (state.currentProject && state.currentProject._id === data.projectId) {
					state.currentProject.buildings = data.project.buildings;
					state.currentProject.city = data.project.city;
					state.currentProject.client = data.project.client;
					state.currentProject.connectedUsers = data.project.connectedUsers;
					state.currentProject.contactPersons = data.project.contactPersons;
					state.currentProject.name = data.project.name;
					state.currentProject.selectedBuilding = data.project.selectedBuilding;
					state.currentProject.selectedFacade = data.project.selectedFacade;
					state.currentProject.selectedFloorOrRoof = data.project.selectedFloorOrRoof;
					state.currentProject.selectedOtherBuilding = data.project.selectedOtherBuilding;
					if (state.currentProject && state.currentProject.selectedFloorOrRoof) {
						await this.updateState({ cart: state.currentProject.selectedFloorOrRoof.cart })
					}

					let allProjects: IProject[] = this.data.projects;
					allProjects.map((project: any) => {
						if (project._id === data.projectId) {
							project.buildings = data.project.buildings;
							project.others = data.project.others;
							project.city = data.project.city;
							project.client = data.project.client;
							project.connectedUsers = data.project.connectedUsers;
							project.contactPersons = data.project.contactPersons;
							project.name = data.project.name;
						}
						return project;
					});
					this.data.updateState({ projects: allProjects });
				}
				await this.setState(state as Interface);
		  })
		}
		await this.setState(state as Interface);
	}

	protected async updateUserCartData (cart: any) {
		await new ModelHttpService().update(ModelName.User, { _id: this.data.currentUser._id, unsortedProducts: cart.map((item: any) => { return item && item._id }) });
		await this.updateState({ cart })
	}

	protected async updateCurentProject (currentProject: any) {
		await new ModelHttpService().update(ModelName.Project, currentProject);
	}

	protected async updateConnectedUserInProject (currentProject: any) {
		let connectedUsers = JSON.parse(JSON.stringify(currentProject.connectedUsers));
		connectedUsers = connectedUsers.map((user: any) => {
			return {
			  user: user.user._id,
			  userAccess: user.userAccess._id,
			};
		  });
		await new ModelHttpService().updateProjectWithSpecificPayload(ModelName.Project, { _id: currentProject._id, connectedUsers });
		let userIds = currentProject.connectedUsers.map((user: any) => {return user.user._id});
		let usersData: any = await new ModelHttpService().findProjectUsersProfile<IUser>(userIds);
		usersData.map((userDetail: any)=> {
		  currentProject && currentProject.connectedUsers.map((user: any) => {
			if (userDetail._id === user.user._id) {
			  user.user.profile = userDetail.profile;
			}
		  });
		});
		await this.setState({ currentProject });
	}

	private async updateLocalProjectState(currentProject: any) {
		await this.setState({ currentProject });
	} 
}
